<template>
  <v-card class="card-container">
    <v-dialog v-model="dialog" :key="dialogKey" max-width="600" persistent>
      <Manage
        :noteId="note.id"
        @close="dialog = false"
        @confirm=";(dialog = false), getNotes()"
      />
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="300px">
      <div class="text-center">
        <v-sheet
          class="px-7 pt-7 pb-4 mx-auto text-center d-inline-block"
          color="blue-grey darken-3"
          dark
        >
          <div class="grey--text text--lighten-1 text-body-2 mb-4">
            {{
              $t('Tem certeza de que deseja excluir esta nota de atualização?')
            }}
          </div>

          <v-btn class="ma-1" color="grey" text @click="deleteDialog = false">
            {{ $t('Cancelar') }}
          </v-btn>

          <v-btn
            class="ma-1"
            color="error"
            text
            @click="remove"
            :loading="loading"
          >
            {{ $t('Excluir') }}
          </v-btn>
        </v-sheet>
      </div>
    </v-dialog>

    <div class="main-toolbar mb-2">
      <div class="icon-back">
        <v-img
          src="../../../assets/figmaIcons/white/patchnotes.svg"
          max-width="55%"
        ></v-img>
      </div>

      <span class="main-toolbar-title">{{ $t('Notas de Atualização') }}</span>

      <v-spacer></v-spacer>

      <v-btn
        color="#0F3565"
        class="hidden-sm-and-down primary-button mr-4"
        depressed
        @click=";(note = {}), (dialogKey = !dialogKey), (dialog = true)"
      >
        {{ $t('Adicionar nota de atualização') }}
      </v-btn>

      <v-btn
        class="hidden-sm-and-down secondary-button"
        depressed
        @click="show = !show"
      >
        {{ show ? $t('Fechar') : $t('Filtros') }}
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-show="show" class="my-2">
        <v-form ref="form">
          <v-row class="ma-0" align="center">
            <v-col cols="12" sm="5">
              <span class="text ml-1">{{ $t('Título') }}</span>
              <v-text-field
                hide-details="auto"
                class="primary-input"
                :placeholder="$t('Pesquisar por Título')"
                v-model="filter.title"
                outlined
                @keyup.enter="applyFilter"
              >
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="3">
              <span class="text ml-1">{{ $t('Data da atualização') }}</span>
              <v-menu
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    hide-details="auto"
                    class="primary-input"
                    placeholder="00/00/0000"
                    :value="formatDate(filter.date)"
                    outlined
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    @keyup.enter="applyFilter"
                  ></v-text-field>
                </template>
                <v-date-picker locale="pt" v-model="filter.date" no-title>
                </v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <div class="d-flex justify-end">
            <v-btn class="clear-button" depressed @click="reset">
              {{ $t('Limpar') }}
            </v-btn>
            <v-btn @click="applyFilter" class="secondary-button" depressed>
              {{ $t('Aplicar filtros') }}
            </v-btn>
          </div>
        </v-form>
      </div>
    </v-expand-transition>

    <v-data-table
      :headers="headers"
      :items="notes"
      :notes-per-page="10"
      class="main-table"
      hide-default-footer
      hide-default-header
      mobile-breakpoint="0"
    >
      <template #header="{ props, on }">
        <thead>
          <tr>
            <th
              class="text-center"
              v-for="header in props.headers"
              :key="header.value"
            >
              <v-simple-checkbox
                v-if="header.value === 'data-table-select'"
                v-model="props.everyItem"
                :indeterminate="props.someItems && !props.everyItem"
                color="purple"
                @input="on['toggle-select-all']"
              ></v-simple-checkbox>

              <div>
                <v-hover v-slot="{ hover }">
                  <div>
                    <span>{{ $t(header.text) }}</span>
                    <v-icon
                      v-if="
                        hover &&
                        header.value != 'data-table-select' &&
                        header.value != 'actions'
                      "
                      color="white"
                      class="mx-2"
                      @click="on.sort(header.value)"
                    >
                      {{
                        props.options.sortBy.includes(header.value)
                          ? `mdi-chevron-down`
                          : `mdi-chevron-up`
                      }}
                    </v-icon>
                  </div>
                </v-hover>
              </div>
            </th>
          </tr>
        </thead>
      </template>

      <template #[`item.note`]="{ item }">
        <span>
          {{ item.note ? item.note : $t('Sem registros') }}
        </span>
      </template>

      <template #[`item.patchDate`]="{ item }">
        <span> {{ formatDate(item.patchDate) }} </span>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              class="ma-1"
              color="#0F3565"
              dark
              small
              depressed
              @click=";(note = item), (dialogKey = !dialogKey), (dialog = true)"
              v-on="on"
            >
              <v-icon dark small> mdi-pencil-outline </v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Editar') }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              class="ma-1"
              color="#921616"
              depressed
              small
              @click=";(note = item), (deleteDialog = true)"
              v-on="on"
            >
              <v-icon color="white" small>mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('Excluir') }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <div class="pagination mx-2">
      <div class="item-pagination block-one">
        <div class="registros" v-if="notes.length">
          <span> 1 - {{ notes.length }} de {{ filter.totalItems }} </span>
          <br />
          <!-- <span>
            Selecionados:
            {{ notes.length }}
          </span> -->
        </div>
      </div>

      <div class="item-pagination block-two">
        <v-pagination
          class="pagination-item"
          v-model="filter.page"
          :length="filter.totalPages"
          :total-visible="5"
        ></v-pagination>
      </div>
    </div>
  </v-card>
</template>

<script>
import Manage from './components/manage.vue'
import FormatDate from '../../../Utils/FormatDate'
import ApiService from '../../../services/ApiService'

export default {
  name: 'PatchNotesPage',

  components: {
    Manage,
  },

  created() {
    this.getNotes()
  },

  data() {
    return {
      loading: false,
      apiService: new ApiService(),
      deleteDialog: false,
      dialog: false,
      dialogKey: false,
      show: false,
      filter: {
        page: 1,
      },
      notes: [],
      note: {},
      headers: [
        {
          text: '',
          align: 'center',
          value: '',
          sortable: false,
        },
        {
          text: 'Título',
          align: 'center',
          value: 'title',
        },
        {
          text: 'Data da Atualização',
          align: 'center',
          value: 'patchDate',
        },
        {
          text: '',
          align: 'center',
          value: 'actions',
          sortable: false,
        },
      ],
    }
  },

  methods: {
    getNotes() {
      this.apiService
        .post('patchNote/list/paginate', this.filter)
        .then((response) => {
          this.notes = response.content.patchNotes

          const pager = response.content.pager
          this.filter.page = pager.currentPage
          this.filter.totalItems = pager.totalItems
          this.filter.totalPages = pager.totalPages
        })
    },

    applyFilter() {
      this.getNotes()
    },

    remove() {
      this.loading = true

      this.apiService
        .delete(`patchNote/delete/${this.note.id}`)
        .then(() => {
          this.$toast.success(
            this.$t('Nota de atualização removida com sucesso!')
          )
          this.getNotes()
          this.deleteDialog = false
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    reset() {
      this.filter = {
        page: 1,
      }

      this.getNotes()
    },

    formatDate(date) {
      return FormatDate.dateTime(date)
    },
  },

  watch: {
    'filter.page': {
      deep: true,
      handler() {
        this.getNotes()
      },
    },
  },
}
</script>

<style scoped lang="scss">
.table-custom {
  &::v-deep .v-data-table__wrapper {
    thead {
      border: none !important;

      th {
        border: none !important;
        &:first-child {
          border-radius: 4px 0 0 4px;
        }

        &:last-child {
          border-radius: 0 4px 4px 0;
        }
      }
    }
  }
}
</style>
